import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

import { useAuthContext } from '../../context/auth-context';
import { useIsMobile } from '../../hooks/use-is-mobile';
import {
  useGetPermissionsCatalogQuery,
  useGetUserPermissionsQuery,
  useUpdateUserPermissionsMutation,
} from '../../redux/services';
import { groupPermissions, permissionLabel } from '../../constants/permission-labels';
import { roleLabel } from '../../constants/roles';

/**
 * Editor de overrides de permisos (`permissions.manage`).
 *
 * El modelo del backend es `efectivos = (defaults[rol] ∪ grant) − deny`, pero pedirle
 * a una persona que piense en dos listas es pedirle que reconstruya esa fórmula de
 * cabeza. Aquí solo se marca lo que la cuenta puede hacer, y el diálogo deduce los
 * overrides comparando con los defaults del rol: encendido que el rol no da → grant;
 * apagado que el rol sí da → deny. Volver a coincidir con el rol borra el override,
 * que es lo que hace el botón «Restablecer».
 */
export const PermissionsDialog = ({ open, user, onClose }) => {
  const { user: actor } = useAuthContext();
  const isMobile = useIsMobile();

  const { data: catalog, isFetching: loadingCatalog } = useGetPermissionsCatalogQuery(undefined, {
    skip: !open,
  });
  const {
    data: current,
    isFetching: loadingUser,
    isError,
  } = useGetUserPermissionsQuery(user?._id, { skip: !open || !user?._id });
  const [updateUserPermissions, { isLoading: saving }] = useUpdateUserPermissionsMutation();

  const [enabled, setEnabled] = useState(() => new Set());
  const [error, setError] = useState(null);

  const defaults = useMemo(() => current?.defaults || [], [current]);

  // Se resiembra con la respuesta del servidor, no con lo que la tabla trajera: los
  // efectivos ya vienen resueltos y así el diálogo no recalcula la fórmula por su cuenta.
  useEffect(() => {
    if (!open || !current) return;
    setEnabled(new Set(current.effective || []));
    setError(null);
  }, [open, current]);

  const groups = useMemo(() => groupPermissions(catalog?.permissions || []), [catalog]);

  const { grant, deny } = useMemo(() => {
    const defaultSet = new Set(defaults);
    return {
      grant: [...enabled].filter(p => !defaultSet.has(p)).sort(),
      deny: defaults.filter(p => !enabled.has(p)).sort(),
    };
  }, [enabled, defaults]);

  const overridesActuales = current?.overrides || { grant: [], deny: [] };
  const sinCambios =
    grant.join() === [...(overridesActuales.grant || [])].sort().join() &&
    deny.join() === [...(overridesActuales.deny || [])].sort().join();

  const toggle = permission =>
    setEnabled(prev => {
      const next = new Set(prev);
      if (next.has(permission)) next.delete(permission);
      else next.add(permission);
      return next;
    });

  const handleSubmit = async () => {
    setError(null);
    try {
      await updateUserPermissions({ id: user._id, grant, deny }).unwrap();
      onClose();
    } catch (err) {
      setError(err?.data?.message || 'No se pudieron guardar los permisos.');
    }
  };

  const cargando = loadingCatalog || loadingUser;
  const esUnoMismo = actor?._id && user?._id && actor._id === user._id;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pb: 1 }}>
        Permisos de @{user?.username}
        <Typography variant="body2" color="text.secondary">
          Rol {roleLabel(current?.role || user?.role)}
          {current?.zone ? ` · ${current.zone}` : ''}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {cargando && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {isError && !cargando && (
          <Alert severity="error">No se pudieron cargar los permisos de este usuario.</Alert>
        )}

        {!cargando && !isError && (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}

            {esUnoMismo && (
              <Alert severity="warning">
                Estás editando tu propia cuenta. Si te quitas un permiso, perderás el
                acceso correspondiente en cuanto la sesión se refresque.
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary">
              Lo marcado es lo que esta cuenta puede hacer. Lo que se aparte de lo que
              da el rol queda guardado como excepción.
            </Typography>

            {groups.map(group => (
              <Box key={group.key}>
                <Typography variant="overline" color="text.secondary">
                  {group.label}
                </Typography>
                <Divider />
                {group.items.map(permission => {
                  const esDelRol = defaults.includes(permission);
                  const activo = enabled.has(permission);
                  return (
                    <Box
                      key={permission}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        py: 0.5,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2">{permissionLabel(permission)}</Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {permission}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        {activo && !esDelRol && (
                          <Chip size="small" color="success" variant="outlined" label="Añadido" />
                        )}
                        {!activo && esDelRol && (
                          <Chip size="small" color="warning" variant="outlined" label="Quitado" />
                        )}
                        <Switch
                          size="small"
                          checked={activo}
                          onChange={() => toggle(permission)}
                          inputProps={{ 'aria-label': permissionLabel(permission) }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button
          onClick={() => setEnabled(new Set(defaults))}
          disabled={cargando || saving || (grant.length === 0 && deny.length === 0)}
        >
          Restablecer al rol
        </Button>
        <Box>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={cargando || saving || sinCambios}
          >
            {saving ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PermissionsDialog;
